import React, { useEffect, useMemo, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import '../pagination/pagination.css';
import { IoAddSharp } from 'react-icons/io5';
import Pagination from '../pagination/pagination';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import NoAccess from '../../../Routes/noAccess';
import api from '../../services/saleVoucherRAApi';
import { fmt3 } from './saleVoucherHelpers';
import { getSelectedFinancialYear, parseApiDate, selectStyles, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';
import { SALE_VOUCHER_CREATE, readPermission, saleVoucherEdit, saleVoucherView } from './saleVoucherConstants';
import { costCentreLabel } from './CostCentreSelects';

const PAGE_SIZE = 25;
const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
];

const defaultFilters = () => ({
    voucherNo: '',
    invoiceNo: '',
    customerNo: '',
    voucherType: '',
    costCenter1: '',
    costCenter2: '',
    status: '',
    fromDate: moment().startOf('month').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
});

// The query string is the source of truth, so Back and refresh restore the same view.
const filtersFromParams = (params) => {
    const d = defaultFilters();
    const out = {};
    Object.keys(d).forEach((key) => { out[key] = params.has(key) ? params.get(key) : d[key]; });
    return out;
};

const fmtDate = (value) => {
    const d = parseApiDate(value);
    return d ? moment(d).format('DD-MMM-YYYY') : '';
};

const StatusBadge = ({ data }) => {
    if (!data || data.isTotal) return null;
    return (
        <span className={`badge ${data.isApproved ? 'bg-success' : 'bg-warning text-dark'}`}>
            {data.isApproved ? 'Approved' : 'Pending'}
        </span>
    );
};

// Edit availability comes from the server (canEdit), never from isApproved.
const ActionCell = ({ data, context }) => {
    if (!data || data.isTotal) return null;
    return (
        <div className="d-flex gap-1">
            <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => context.onView(data)}>View</button>
            {context.canEditPermission && (
                <span title={data.canEdit ? '' : 'Approved vouchers cannot be edited.'}>
                    <button
                        type="button" className="btn btn-sm btn-outline-primary py-0" disabled={!data.canEdit}
                        style={data.canEdit ? undefined : { pointerEvents: 'none' }} onClick={() => context.onEdit(data)}
                    >
                        Edit
                    </button>
                </span>
            )}
        </div>
    );
};

const SaleVoucherRAList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [permissions] = useState(() => readPermission());
    const [FilterTab, setFilterTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [highlight] = useState(location.state?.highlightVoucherNo || '');
    const [lookups, setLookups] = useState({ voucherTypes: [], costCentre1: [], settings: null });
    const [parties, setParties] = useState([]);
    const [costCentre2, setCostCentre2] = useState([]);

    const applied = useMemo(() => filtersFromParams(searchParams), [searchParams]);
    const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);
    const [draft, setDraft] = useState(applied);
    const financialYear = useMemo(() => getSelectedFinancialYear(), []);

    // Keep the form in step with the URL (Back / Forward / Reset).
    useEffect(() => { setDraft(applied); }, [applied]);

    useEffect(() => {
        (async () => {
            const [dd, settings, party, level1] = await Promise.all([
                api.getDropdowns(['VoucherTypes']), api.getSettings(), api.getPartyCustomers({ partyType: 'All' }),
                api.getCostCentreList({ level: 1 }), // GetCostCentres is the single source for every level
            ]);
            if (!dd.ok) { toast.error(dd.message); return; }
            setLookups({ voucherTypes: dd.dropdowns?.VoucherTypes || [], costCentre1: level1, settings: settings.ok ? settings.result : null });
            if (party.ok) setParties(party.result || []);
        })();
    }, []);

    // Level 2 depends on the chosen level 1.
    useEffect(() => {
        if (!draft.costCenter1) { setCostCentre2([]); return undefined; }
        let cancelled = false;
        api.getCostCentreList({ level: 2, costCenter1: draft.costCenter1 }).then((list) => { if (!cancelled) setCostCentre2(list); });
        return () => { cancelled = true; };
    }, [draft.costCenter1]);

    // `refresh` in the router state is set after a save so returning here always refetches.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await api.getVouchers({ ...applied, financialYear, pageNo: page, pageSize });
            if (cancelled) return;
            setLoading(false);
            if (res.status === 204) {
                setRowData([]);
                setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
            } else if (res.ok) {
                const rows = res.result || [];
                setRowData(rows);
                const totalRecords = rows[0]?.totalRecords ?? rows.length;
                setPagination(res.pagination || { currentPage: page, totalPages: Math.max(Math.ceil(totalRecords / pageSize), 1), totalRecords });
            } else {
                setRowData([]);
                toast.error(res.message);
            }
        })();
        return () => { cancelled = true; };
    }, [applied, page, pageSize, financialYear, location.state?.refresh]);

    const writeParams = (filters, pageNo) => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && `${value}`.trim() !== '') next.set(key, `${value}`.trim());
        });
        next.set('page', pageNo);
        setSearchParams(next);
    };

    const handleSearch = () => writeParams(draft, 1);
    const handleReset = () => writeParams(defaultFilters(), 1);
    const handlePage = (pageNo) => writeParams(applied, pageNo);
    const setDraftField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const columnDefs = useMemo(() => [
        { headerName: '', width: 130, pinned: 'left', suppressMovable: true, sortable: false, cellRenderer: ActionCell },
        { headerName: 'Voucher No', field: 'voucherNo', filter: true, floatingFilter: true, width: 140, pinned: 'left', suppressMovable: true },
        { headerName: 'Date', field: 'postingDate', filter: true, floatingFilter: true, width: 120, suppressMovable: true, valueFormatter: (p) => fmtDate(p.value) },
        { headerName: 'Type', field: 'voucherType', filter: true, floatingFilter: true, width: 150, suppressMovable: true },
        {
            headerName: 'Debit Account', field: 'customerName', filter: true, floatingFilter: true, width: 260, suppressMovable: true,
            valueGetter: (p) => (p.data?.customerNo ? `${p.data.customerNo} — ${p.data.customerName || ''}` : ''),
        },
        { headerName: 'Invoice No', field: 'invoiceNo', filter: true, floatingFilter: true, width: 130, suppressMovable: true },
        { headerName: 'Invoice Date', field: 'invoiceDate', width: 120, suppressMovable: true, valueFormatter: (p) => fmtDate(p.value) },
        { headerName: 'Due Date', field: 'dueDate', width: 120, suppressMovable: true, valueFormatter: (p) => fmtDate(p.value) },
        { headerName: 'Currency', field: 'currency', width: 100, suppressMovable: true },
        {
            headerName: 'Amount', field: 'amount', filter: true, floatingFilter: true, width: 130, suppressMovable: true, type: 'rightAligned',
            valueFormatter: (p) => (p.value === null || p.value === undefined ? '' : fmt3(p.value)),
        },
        {
            headerName: 'Converted Amount', field: 'convertedAmount', width: 150, suppressMovable: true, type: 'rightAligned',
            valueFormatter: (p) => (p.value === null || p.value === undefined ? '' : fmt3(p.value)),
        },
        {
            headerName: 'Amount Received', field: 'amountReceived', width: 150, suppressMovable: true, type: 'rightAligned',
            valueFormatter: (p) => (p.value === null || p.value === undefined ? '' : fmt3(p.value)),
        },
        { headerName: 'Status', field: 'isApproved', width: 110, suppressMovable: true, cellRenderer: StatusBadge },
        { headerName: 'Created By', field: 'createdBy', filter: true, floatingFilter: true, width: 140, suppressMovable: true },
    ], []);

    const pageTotal = useMemo(() => rowData.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [rowData]);
    const pinnedBottomRowData = useMemo(
        () => (rowData.length ? [{ voucherNo: 'Page Total', amount: pageTotal, isTotal: true }] : []),
        [rowData, pageTotal],
    );

    const partyOptions = parties.map((p) => ({ value: p.customerNo, label: `${p.customerNo} — ${p.customerName}` }));
    const typeOptions = lookups.voucherTypes.map((t) => ({ value: t.value, label: t.text }));
    const cc1Options = lookups.costCentre1.map((c) => ({ value: c.value, label: c.text }));
    const cc2Options = costCentre2.map((c) => ({ value: c.value, label: c.text }));
    const pick = (options, value) => (value ? options.find((o) => o.value === value) || { value, label: value } : null);
    const select = (label, options, key, extra = {}) => (
        <div>
            <label className="form-label">{label}</label>
            <WindowedSelect
                className="p-0"
                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                styles={selectStyles}
                isClearable
                value={pick(options, draft[key])}
                onChange={(opt) => setDraftField(key, opt?.value || '')}
                options={options}
                {...extra}
            />
        </div>
    );
    const textFilter = (label, key) => (
        <div>
            <label className="form-label">{label}</label>
            <input type="text" className="p-2 form-control" placeholder={label} value={draft[key]} onChange={(e) => setDraftField(key, e.target.value)} />
        </div>
    );

    return (
        <div className="p-3">
            {permissions?.canView === true ? (
                <>
                    <div className="cost-center-section">
                        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                            <div className="param-label pt-1">Search Parameters</div>
                        </div>
                        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                    {textFilter('Voucher No', 'voucherNo')}
                                    {textFilter('Invoice No', 'invoiceNo')}
                                    {select('Debit Account', partyOptions, 'customerNo')}
                                    {select('Type', typeOptions, 'voucherType')}
                                    {select(costCentreLabel(lookups.settings, 1), cc1Options, 'costCenter1', {
                                        onChange: (opt) => setDraft((prev) => ({ ...prev, costCenter1: opt?.value || '', costCenter2: '' })),
                                    })}
                                    {select(costCentreLabel(lookups.settings, 2), cc2Options, 'costCenter2', { isDisabled: !draft.costCenter1 })}
                                    {select('Status', STATUS_OPTIONS, 'status', { isClearable: false, value: STATUS_OPTIONS.find((o) => o.value === draft.status) || STATUS_OPTIONS[0] })}
                                    <div>
                                        <label className="form-label">From Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.fromDate)}
                                            onChange={(d) => setDraftField('fromDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" isClearable
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">To Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.toDate)}
                                            onChange={(d) => setDraftField('toDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" isClearable
                                        />
                                    </div>
                                    <div className="d-flex gap-2 align-items-end">
                                        <div onClick={handleSearch} className="mainbtnGry flex-end gap-2 w-50">
                                            <FaFileCircleExclamation color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Search</div>
                                        </div>
                                        <div onClick={handleReset} className="mainbtnRed flex-end gap-2 w-50">
                                            <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Reset</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-box2 p-3 mt-2">
                        <div className="d-flex justify-contant-between">
                            <h5 className="col mainInvHead">Sale Voucher List</h5>
                            <div className="d-flex justify-content-end gap-1">
                                {permissions?.canAdd === true && (
                                    <div className="col center">
                                        <button className="mainbtn" type="button" onClick={() => navigate(SALE_VOUCHER_CREATE)}>
                                            <IoAddSharp color="#fff" className="center mt-1" /> <div>New Voucher</div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <div>Loading...</div>
                        ) : rowData.length === 0 ? (
                            <div className="text-center text-muted p-4">No sale vouchers found for the selected filters.</div>
                        ) : (
                            <div className="gridshad mt-2">
                                <div className="ag-theme-quartz" style={{ height: 650 }}>
                                    <AgGridReact
                                        getRowStyle={(params) => {
                                            if (params.data?.isTotal) return { background: '#E8E4F0', fontWeight: 'bold' };
                                            if (highlight && params.data?.voucherNo === highlight) return { background: '#FFF3C4' };
                                            return params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' };
                                        }}
                                        rowData={rowData}
                                        enableFilter={true}
                                        floatingFilter={true}
                                        columnDefs={columnDefs}
                                        pinnedBottomRowData={pinnedBottomRowData}
                                        context={{
                                            canEditPermission: permissions?.canEdit === true,
                                            onView: (row) => navigate(saleVoucherView(row.voucherNo, row.financialYear)),
                                            onEdit: (row) => navigate(saleVoucherEdit(row.voucherNo, row.financialYear)),
                                        }}
                                    />
                                </div>
                                <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={handlePage} />
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <NoAccess />
            )}
        </div>
    );
};

export default SaleVoucherRAList;
